"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { FEATURE_FLAG_KEYS, isAssistantClientEnabled } from "@agroproj/backend/convex/assistant/config";
import { getAssistantRouteContext } from "@agroproj/backend/convex/assistant/routeContext";
import { useQuery } from "convex/react";
import { BotIcon, SendIcon, ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { type FormEvent, type MouseEvent, useMemo, useState, useEffect, useRef } from "react";
import { AssistantProjectConfirmation, getProjectProposalIds } from "@/components/assistant/assistant-project-confirmation";
import { AssistantSuggestions } from "@/components/assistant/assistant-suggestions";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { metaTextClassName } from "@/components/ui/typography";
import { useAssistantChat } from "@/hooks/use-assistant-chat";
import { useConvexFeatureFlag } from "@/hooks/use-convex-feature-flag";
import { getAssistantDomainDescription, getAssistantPlaceholder } from "@/lib/assistant/suggestions";
import { dateFormat } from "@/lib/date-format";
import { capturePosthogEvent } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { AssistantHistory } from "./assistant-history";
type MessagePartLike = {
  type?: unknown;
  text?: unknown;
};
function getPartText(part: unknown) {
  if (!part || typeof part !== "object") return null;
  const maybePart = part as MessagePartLike;
  if (maybePart.type === "text" && typeof maybePart.text === "string") {
    return maybePart.text;
  }
  return null;
}
function AssistantMessage(t0) {
  const $ = _c(19);
  const {
    role,
    parts,
    feedback,
    onFeedback
  } = t0;
  let canGiveFeedback;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== onFeedback || $[1] !== parts || $[2] !== role) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const text = parts.map(getPartText).filter(_temp).join("");
      const proposalIds = role === "assistant" ? getProjectProposalIds(parts) : [];
      if (!text.trim() && proposalIds.length === 0) {
        t4 = null;
        break bb0;
      }
      canGiveFeedback = role === "assistant" && onFeedback;
      const t5 = role === "user" ? "bg-primary text-primary-foreground ml-auto border-transparent" : "bg-card text-card-foreground";
      if ($[8] !== t5) {
        t1 = cn("max-w-[92%] rounded-lg border px-3 py-2 text-xs/relaxed", t5);
        $[8] = t5;
        $[9] = t1;
      } else {
        t1 = $[9];
      }
      t2 = text.trim() ? <p className="whitespace-pre-wrap">{text}</p> : null;
      t3 = proposalIds.map(_temp2);
    }
    $[0] = onFeedback;
    $[1] = parts;
    $[2] = role;
    $[3] = canGiveFeedback;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    canGiveFeedback = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[10] !== canGiveFeedback || $[11] !== feedback || $[12] !== onFeedback) {
    t5 = canGiveFeedback ? <div className="mt-2 flex items-center gap-1"><Button aria-label={"Resposta \xFAtil"} aria-pressed={feedback === "positive"} onClick={event => onFeedback(event, "positive")} size="icon-sm" type="button" variant={feedback === "positive" ? "secondary" : "ghost"}><ThumbsUpIcon /></Button><Button aria-label={"Resposta pouco \xFAtil"} aria-pressed={feedback === "negative"} onClick={event_0 => onFeedback(event_0, "negative")} size="icon-sm" type="button" variant={feedback === "negative" ? "secondary" : "ghost"}><ThumbsDownIcon /></Button></div> : null;
    $[10] = canGiveFeedback;
    $[11] = feedback;
    $[12] = onFeedback;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t5) {
    t6 = <div className={t1}>{t2}{t3}{t5}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
function _temp2(proposalId) {
  return <AssistantProjectConfirmation key={proposalId} proposalId={proposalId} />;
}
function _temp(part) {
  return part !== null;
}
export function AssistantPanel() {
  const $ = _c(1);
  const {
    enabled: convexEnabled,
    isLoading
  } = useConvexFeatureFlag(FEATURE_FLAG_KEYS.ASSISTANT);
  const [lastResolvedEnabled, setLastResolvedEnabled] = useState(false);
  if (!isLoading && lastResolvedEnabled !== convexEnabled) {
    setLastResolvedEnabled(convexEnabled);
  }
  const shouldRender = isAssistantClientEnabled() && (isLoading ? lastResolvedEnabled : convexEnabled);
  if (!shouldRender) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <AssistantPanelContent />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function AssistantPanelContent() {
  const $ = _c(117);
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = getAssistantRouteContext(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routeContext = t0;
  let t1;
  if ($[2] !== routeContext) {
    t1 = getAssistantPlaceholder(routeContext);
    $[2] = routeContext;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const placeholder = t1;
  let t2;
  if ($[4] !== routeContext) {
    t2 = getAssistantDomainDescription(routeContext);
    $[4] = routeContext;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const domainDescription = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const historyAvailable = useQuery(api.assistant.history.available, t3);
  const [threadId, setThreadId] = useState();
  let t4;
  if ($[7] !== threadId) {
    t4 = threadId ? {
      threadId
    } : "skip";
    $[7] = threadId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const thread = useQuery(api.assistant.history.read, t4);
  let t5;
  if ($[9] !== thread?.revision || $[10] !== threadId) {
    t5 = threadId ? {
      threadId,
      revision: thread?.revision ?? 0
    } : undefined;
    $[9] = thread?.revision;
    $[10] = threadId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    messages,
    sendMessage,
    status,
    error,
    setMessages,
    stop
  } = useAssistantChat(routeContext, t5);
  const loadedThread = useRef(undefined);
  let t6;
  let t7;
  if ($[12] !== setMessages || $[13] !== status || $[14] !== thread || $[15] !== threadId) {
    t6 = () => {
      if (threadId && thread && status !== "submitted" && status !== "streaming" && loadedThread.current !== `${threadId}:${thread.revision}:${thread.turns.map(_temp3).join()}`) {
        setMessages(thread.turns.flatMap(_temp4));
        loadedThread.current = `${threadId}:${thread.revision}:${thread.turns.map(_temp5).join()}`;
      }
    };
    t7 = [threadId, thread, status, setMessages];
    $[12] = setMessages;
    $[13] = status;
    $[14] = thread;
    $[15] = threadId;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  const previousOrganization = useRef(routeContext.organizationSlug);
  let t8;
  let t9;
  if ($[18] !== routeContext.organizationSlug || $[19] !== setMessages || $[20] !== stop) {
    t8 = () => {
      if (previousOrganization.current === routeContext.organizationSlug) {
        return;
      }
      previousOrganization.current = routeContext.organizationSlug;
      stop();
      setThreadId(undefined);
      setMessages([]);
      loadedThread.current = undefined;
    };
    t9 = [routeContext.organizationSlug, stop, setMessages];
    $[18] = routeContext.organizationSlug;
    $[19] = setMessages;
    $[20] = stop;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[23] !== setMessages || $[24] !== stop || $[25] !== thread || $[26] !== threadId) {
    t10 = () => {
      if (threadId && thread === null) {
        stop();
        setMessages([]);
      }
    };
    t11 = [threadId, thread, stop, setMessages];
    $[23] = setMessages;
    $[24] = stop;
    $[25] = thread;
    $[26] = threadId;
    $[27] = t10;
    $[28] = t11;
  } else {
    t10 = $[27];
    t11 = $[28];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[29] !== historyAvailable || $[30] !== setMessages || $[31] !== stop || $[32] !== threadId) {
    t12 = () => {
      if (historyAvailable === false && threadId) {
        stop();
        setThreadId(undefined);
        setMessages([]);
        loadedThread.current = undefined;
      }
    };
    t13 = [historyAvailable, threadId, stop, setMessages];
    $[29] = historyAvailable;
    $[30] = setMessages;
    $[31] = stop;
    $[32] = threadId;
    $[33] = t12;
    $[34] = t13;
  } else {
    t12 = $[33];
    t13 = $[34];
  }
  useEffect(t12, t13);
  const [input, setInput] = useState("");
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {};
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const [feedbackByMessage, setFeedbackByMessage] = useState(t14);
  const [isOpen, setIsOpen] = useState(false);
  const isBusy = status === "submitted" || status === "streaming";
  let t15;
  if ($[36] !== thread || $[37] !== threadId) {
    t15 = threadId && (!thread || thread.archived || thread.busy || thread.turns.some(_temp6));
    $[36] = thread;
    $[37] = threadId;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const blockedHistory = Boolean(t15);
  let t16;
  if ($[39] !== blockedHistory || $[40] !== input || $[41] !== isBusy) {
    t16 = input.trim().length > 0 && !isBusy && !blockedHistory;
    $[39] = blockedHistory;
    $[40] = input;
    $[41] = isBusy;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const canSend = t16;
  let t17;
  if ($[43] !== blockedHistory || $[44] !== isBusy || $[45] !== sendMessage) {
    t17 = async function submitPrompt(prompt) {
      const trimmed = prompt.trim();
      if (!trimmed || isBusy || blockedHistory) {
        return;
      }
      setInput("");
      await sendMessage({
        text: trimmed
      });
    };
    $[43] = blockedHistory;
    $[44] = isBusy;
    $[45] = sendMessage;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const submitPrompt = t17;
  let t18;
  if ($[47] !== input || $[48] !== submitPrompt) {
    t18 = async function handleSubmit(event) {
      event.preventDefault();
      await submitPrompt(input);
    };
    $[47] = input;
    $[48] = submitPrompt;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  const handleSubmit = t18;
  let t19;
  if ($[50] !== routeContext.domain || $[51] !== routeContext.recordType) {
    t19 = function submitFeedback(event_0, messageId, rating) {
      event_0.stopPropagation();
      setFeedbackByMessage(current => ({
        ...current,
        [messageId]: rating
      }));
      capturePosthogEvent("AgroProj:Assistant:FeedbackSubmitted", {
        rating,
        domain: routeContext.domain,
        recordType: routeContext.recordType ?? "none"
      });
    };
    $[50] = routeContext.domain;
    $[51] = routeContext.recordType;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  const submitFeedback = t19;
  const t20 = isOpen && "hidden";
  let t21;
  if ($[53] !== t20) {
    t21 = cn("fixed right-4 bottom-4 z-60 shadow-lg", t20);
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t21) {
    t22 = <SheetTrigger render={<Button aria-label="Abrir assistente" className={t21} size="icon-sm" variant="secondary" />} />;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <BotIcon />;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t22) {
    t24 = <TooltipTrigger render={t22}>{t23}</TooltipTrigger>;
    $[58] = t22;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <TooltipContent side="left">Assistente</TooltipContent>;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t24) {
    t26 = <Tooltip>{t24}{t25}</Tooltip>;
    $[61] = t24;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <SheetTitle>Assistente AgroProj</SheetTitle>;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== domainDescription) {
    t28 = <SheetHeader className="shrink-0 border-b px-4 py-3 pr-12">{t27}<SheetDescription>{domainDescription}</SheetDescription></SheetHeader>;
    $[64] = domainDescription;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  const t29 = routeContext.organizationSlug ?? "";
  let t30;
  if ($[66] !== setMessages || $[67] !== stop) {
    t30 = id => {
      stop();
      setMessages([]);
      setThreadId(id);
      loadedThread.current = undefined;
    };
    $[66] = setMessages;
    $[67] = stop;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== isBusy || $[70] !== t29 || $[71] !== t30 || $[72] !== threadId) {
    t31 = <AssistantHistory organizationSlug={t29} selected={threadId} busy={isBusy} onSelect={t30} />;
    $[69] = isBusy;
    $[70] = t29;
    $[71] = t30;
    $[72] = threadId;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== blockedHistory || $[75] !== thread || $[76] !== threadId) {
    t32 = threadId && thread ? <p className="px-4 py-2 text-xs/relaxed">Expira em {dateFormat(thread.expiresAt)}.{" "}{thread.truncated ? "Exibindo apenas os turnos mais recentes." : ""}{" "}{blockedHistory ? "Esta conversa n\xE3o pode continuar agora. Inicie uma nova conversa." : ""}</p> : null;
    $[74] = blockedHistory;
    $[75] = thread;
    $[76] = threadId;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== feedbackByMessage || $[79] !== messages || $[80] !== routeContext || $[81] !== submitFeedback || $[82] !== submitPrompt) {
    t33 = messages.length === 0 ? <AssistantSuggestions context={routeContext} onPrompt={submitPrompt} /> : messages.map(message => <AssistantMessage key={message.id} feedback={feedbackByMessage[message.id]} onFeedback={message.role === "assistant" ? (event_1, rating_0) => submitFeedback(event_1, message.id, rating_0) : undefined} parts={message.parts as unknown[]} role={message.role} />);
    $[78] = feedbackByMessage;
    $[79] = messages;
    $[80] = routeContext;
    $[81] = submitFeedback;
    $[82] = submitPrompt;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== isBusy) {
    t34 = isBusy ? <output aria-live="polite" className={metaTextClassName}>Pensando...</output> : null;
    $[84] = isBusy;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== error) {
    t35 = error ? <Alert variant="destructive"><AlertDescription>O assistente não conseguiu responder agora.</AlertDescription></Alert> : null;
    $[86] = error;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t33 || $[89] !== t34 || $[90] !== t35) {
    t36 = <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain p-4">{t33}{t34}{t35}</div>;
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  const t37 = isBusy || blockedHistory;
  let t38;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = event_2 => setInput(event_2.target.value);
    $[92] = t38;
  } else {
    t38 = $[92];
  }
  let t39;
  if ($[93] !== input || $[94] !== placeholder || $[95] !== t37) {
    t39 = <Textarea aria-label="Mensagem para o assistente" className="max-h-32 min-h-20 resize-none" disabled={t37} onChange={t38} placeholder={placeholder} value={input} />;
    $[93] = input;
    $[94] = placeholder;
    $[95] = t37;
    $[96] = t39;
  } else {
    t39 = $[96];
  }
  const t40 = !canSend;
  let t41;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <SendIcon />;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  const t42 = isBusy ? "Enviando..." : "Enviar";
  let t43;
  if ($[98] !== t40 || $[99] !== t42) {
    t43 = <div className="mt-2 flex justify-end"><Button disabled={t40} size="sm" type="submit">{t41}{t42}</Button></div>;
    $[98] = t40;
    $[99] = t42;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  let t44;
  if ($[101] !== handleSubmit || $[102] !== t39 || $[103] !== t43) {
    t44 = <form className="shrink-0 border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" onSubmit={handleSubmit}>{t39}{t43}</form>;
    $[101] = handleSubmit;
    $[102] = t39;
    $[103] = t43;
    $[104] = t44;
  } else {
    t44 = $[104];
  }
  let t45;
  if ($[105] !== t36 || $[106] !== t44) {
    t45 = <div className="flex min-h-0 flex-1 flex-col">{t36}{t44}</div>;
    $[105] = t36;
    $[106] = t44;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] !== t28 || $[109] !== t31 || $[110] !== t32 || $[111] !== t45) {
    t46 = <SheetContent className="flex size-full flex-col gap-0 p-0 sm:max-w-md" showCloseButton={true} side="right">{t28}{t31}{t32}{t45}</SheetContent>;
    $[108] = t28;
    $[109] = t31;
    $[110] = t32;
    $[111] = t45;
    $[112] = t46;
  } else {
    t46 = $[112];
  }
  let t47;
  if ($[113] !== isOpen || $[114] !== t26 || $[115] !== t46) {
    t47 = <Sheet open={isOpen} onOpenChange={setIsOpen}>{t26}{t46}</Sheet>;
    $[113] = isOpen;
    $[114] = t26;
    $[115] = t46;
    $[116] = t47;
  } else {
    t47 = $[116];
  }
  return t47;
}
function _temp6(turn_2) {
  return turn_2.unavailable;
}
function _temp5(turn_0) {
  return turn_0.unavailable;
}
function _temp4(turn) {
  return [{
    id: `${turn.id}-u`,
    role: "user" as const,
    parts: [{
      type: "text" as const,
      text: turn.userText
    }]
  }, {
    id: `${turn.id}-a`,
    role: "assistant" as const,
    parts: [{
      type: "text" as const,
      text: turn.assistantText
    }]
  }];
}
function _temp3(turn_1) {
  return turn_1.unavailable;
}